import React, { useCallback, useContext, useMemo } from 'react';

import PropTypes from 'prop-types';
import MessageDetailsShape from 'app/shapes/MessageDetailsShape';

import { useGetAvailableBalanceQuery } from 'app/api/mainApi';

import DialogsContext from 'app/contexts/DialogsContext';
import ModalsContext from 'app/contexts/ModalsContext';
import useCreditCardManagement from 'app/hooks/useCreditCardManagement';
import { userHasCard } from 'app/helpers/userHelper';

import {
  Box,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import MessageDetailsControls from 'app/features/mail/components/message_details/MessageDetailsControls';
import MessageDetailsHeader from 'app/features/mail/components/message_details/MessageDetailsHeader';
import PreviewableAttachments from 'app/features/mail/components/message_details/PreviewableAttachments';
import FullPageLoadingSpinner from 'app/features/mail/components/FullPageLoadingSpinner';
import ListingButton from 'app/features/mail/components/message_details/ListingButton';
import SanitizedHTML from 'app/helpers/SanitizedHTML';
import PaymentStatus from 'app/features/mail/components/message_details/PaymentStatus';
import ConfirmPaymentDialog from 'app/features/mail/components/message_details/ConfirmPaymentDialog';

const MessageDetailRow = styled(Box)(() => ({
  '&.MuiBox-root': {
    padding: '8px',
  },
}));

const MessageDescriptionRow = styled(Box)(() => ({
  '&.MuiBox-root': {
    borderTop: '1px solid var(--color_ddd)',
    borderBottom: '1px solid var(--color_ddd)',
    padding: '15px 8px',
    marginTop: '10px',
  },
}));

const DiagonalLinesBox = styled(Box)(() => ({
  backgroundImage: `
    repeating-linear-gradient(
      45deg,
      #eee 0px,
      #eee 1px,
      transparent 1px,
      transparent 10px
    ),
    repeating-linear-gradient(
      -45deg,
      #ccc 0px,
      #ccc 1px,
      transparent 1px,
      transparent 10px
    )
  `,
  padding: '15px',
  border: '1px solid #eee',
}));

const MessageDetails = ({
  fetchReceivedMessageDetails,
  messageDetails,
  isLoading,
  closeFunc,
  currentUser,
  selectedTab,
  maxHeight,
  setSelectedMessageDetails,
}) => {
  // NOTE: Once loading is finished, the messageDetails still must be set by a hook, so we may need one more round trip.
  if (isLoading || !messageDetails) {
    return (
      <FullPageLoadingSpinner />
    );
  }

  // Memoize derived values for performance
  const {
    isPayToView,
    isPaymentRequest,
    requiresPaymentToView,
    isPayable,
  } = useMemo(() => {
    const isPayToView = messageDetails.messageType === 'Pay-to-View';
    const isPaymentRequest = messageDetails.messageType === 'Payment Request';
    const requiresPaymentToView =
      currentUser.id !== messageDetails.sender.id &&
      !messageDetails.isPaid &&
      isPayToView;
    const isPayable =
      currentUser.id !== messageDetails.sender.id &&
      !messageDetails.isPaid &&
      (isPayToView || isPaymentRequest);
    return {
      isPayToView,
      isPaymentRequest,
      requiresPaymentToView,
      isPayable,
    };
  }, [messageDetails]);

  const { openDialog, closeDialog } = useContext(DialogsContext);
  const { openModal, closeModal } = useContext(ModalsContext);

  let currentUserHasCard = userHasCard(currentUser);

  const { data: availableBalance } = useGetAvailableBalanceQuery();
  const availableBalanceValue = parseFloat(availableBalance?.available_balance);

  const openConfirmPaymentDialog = useCallback(() => {
    openDialog({
      component: ConfirmPaymentDialog,
      props: {
        messageDetails,
        title: 'Confirm Payment',
        fetchReceivedMessageDetails,
        setSelectedMessageDetails,
      },
    });
  }, [openDialog, messageDetails, fetchReceivedMessageDetails, setSelectedMessageDetails]);

  const handlePaymentWorkflow = (currentBalance) => {
    closeModal();
    closeDialog();

    const onTryAgainSuccess = () => {};

    const {
      handleAddToBalanceOrManageCards,
    } = useCreditCardManagement({
      openModal,
      currentUser,
      guard: null,
      setGuard: null,
      onTryAgainSuccess,
      onAddToBalanceSuccess: handlePaymentWorkflow,
      onManageCardsSuccess: () => {
        currentUserHasCard = true;
        handlePaymentWorkflow(currentBalance);
      },
    });

    if (currentBalance < messageDetails.paymentAmount) {
      const deficit = parseFloat((messageDetails.paymentAmount - currentBalance).toFixed(2));
      const context = '';
      const assetName = 'payment';
      const customCloseFunc = this;
      const showError = true;

      handleAddToBalanceOrManageCards({
        currentUserHasCard,
        options: {
          deficit,
          context,
          assetName,
          customCloseFunc,
          showError,
        },
      });
    } else {
      openConfirmPaymentDialog();
    }
  };

  return (
    <Box
      sx={{
        maxHeight,
        overflowY: 'auto',
      }}
    >
      <MessageDetailsControls
        closeFunc={closeFunc}
        selectedTab={selectedTab}
      />
      <Stack
        direction="column"
        sx={{
          px: 2.5,
        }}
      >
        <Stack direction="row" sx={{ pt: 2, px: 1 }} spacing={2}>
          { (isPayToView || isPaymentRequest) && (
            <PaymentStatus
              messageDetails={messageDetails}
              currentUser={currentUser}
            />
          )}
          { isPayable && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handlePaymentWorkflow(availableBalanceValue)}
            >
              Send Payment
            </Button>
          )}
        </Stack>
        <MessageDetailRow>
          <MessageDetailsHeader
            messageDetails={messageDetails}
            currentUser={currentUser}
            selectedTab={selectedTab}
          />
        </MessageDetailRow>
        {messageDetails.listing && messageDetails.sender.id !== currentUser.id && (
          <MessageDetailRow>
            <ListingButton
              listing={messageDetails.listing}
            />
          </MessageDetailRow>
        )}
        {messageDetails.description && (
          <MessageDescriptionRow>
            {messageDetails.description}
          </MessageDescriptionRow>
        )}
        { !requiresPaymentToView && (
          <>
            <MessageDetailRow>
              <SanitizedHTML html={messageDetails.body} />
            </MessageDetailRow>
            {messageDetails.attachments.length > 0 && (
              <MessageDetailRow>
                <Typography variant="subtitle1">
                  Attachments
                </Typography>
                <PreviewableAttachments attachments={messageDetails.attachments} />
              </MessageDetailRow>
            )}
          </>
        )}
        { requiresPaymentToView && (
          <MessageDetailRow>
            <Typography variant="subtitle1">
              Attachments
            </Typography>
            <DiagonalLinesBox sx={{ minHeight: 100 }}>
              This message is locked. Please pay to unlock.
              {messageDetails.attachments.map((attachment) => (
                <React.Fragment key={attachment.id}>
                  <Box>
                    {attachment.filename}
                  </Box>
                  <Box>
                    {attachment.sizeString}
                  </Box>
                </React.Fragment>
              ))}
            </DiagonalLinesBox>
          </MessageDetailRow>
        )}
      </Stack>
    </Box>
  );
};

MessageDetails.defaultProps = {
  messageDetails: null,
};

MessageDetails.propTypes = {
  fetchReceivedMessageDetails: PropTypes.func.isRequired,
  messageDetails: MessageDetailsShape,
  isLoading: PropTypes.bool.isRequired,
  closeFunc: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  selectedTab: PropTypes.string.isRequired,
  maxHeight: PropTypes.object.isRequired,
  setSelectedMessageDetails: PropTypes.func.isRequired,
};

export default MessageDetails;
